// client/src/components/StateSelect.tsx
import React, { useState } from 'react';

interface StateSelectProps {
    value: string[];
    onChange: (states: string[]) => void;
    multiple?: boolean;
    required?: boolean;
    className?: string;
}

const US_STATES = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
];

export default function StateSelect({
                                        value,
                                        onChange,
                                        multiple = false,
                                        required = false,
                                        className = '',
                                    }: StateSelectProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    // Single select mode — use a regular dropdown
    if (!multiple) {
        return (
            <select
                value={value[0] || ''}
                onChange={e => onChange([e.target.value])}
                required={required}
                className={className}
            >
                <option value="">Select a state</option>
                {US_STATES.map(({ code, name }) => (
                    <option key={code} value={code}>{name}</option>
                ))}
            </select>
        );
    }

    // Multi select mode — checkbox picker
    const toggleState = (code: string) => {
        if (value.includes(code)) {
            onChange(value.filter(s => s !== code));
        } else {
            onChange([...value, code]);
        }
    };

    const filtered = search
        ? US_STATES.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.code.toLowerCase().includes(search.toLowerCase()))
        : US_STATES;

    return (
        <div className="relative">
            {/* Selected states display */}
            <div
                onClick={() => setOpen(!open)}
                className={`${className} cursor-pointer min-h-[42px] flex flex-wrap gap-1 items-center`}
            >
                {value.length === 0 ? (
                    <span className="text-gray-400 text-sm">Click to select states...</span>
                ) : (
                    <>
                        {value.map(code => (
                            <span key={code} className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {code}
                                <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); toggleState(code); }}
                                    className="ml-1 text-blue-500 hover:text-blue-800"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </>
                )}
            </div>

            {/* Dropdown */}
            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                    {/* Search */}
                    <div className="p-2 border-b sticky top-0 bg-white">
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search states..."
                            className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            onClick={e => e.stopPropagation()}
                        />
                    </div>

                    {/* Quick actions */}
                    <div className="px-2 py-1 border-b bg-gray-50 flex gap-2">
                        <button type="button" onClick={e => { e.stopPropagation(); onChange(US_STATES.map(s => s.code)); }} className="text-xs text-blue-600 hover:text-blue-800">Select All</button>
                        <button type="button" onClick={e => { e.stopPropagation(); onChange([]); }} className="text-xs text-red-600 hover:text-red-800">Clear All</button>
                        <button type="button" onClick={e => { e.stopPropagation(); setOpen(false); setSearch(''); }} className="text-xs text-gray-600 hover:text-gray-800 ml-auto">Done</button>
                    </div>

                    {/* State list */}
                    <div className="overflow-y-auto max-h-48">
                        {filtered.map(({ code, name }) => (
                            <label
                                key={code}
                                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                                onClick={e => e.stopPropagation()}
                            >
                                <input
                                    type="checkbox"
                                    checked={value.includes(code)}
                                    onChange={() => toggleState(code)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                                />
                                <span className="text-gray-700">{name}</span>
                                <span className="text-gray-400 ml-auto text-xs">{code}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Click outside to close */}
            {open && (
                <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearch(''); }} />
            )}
        </div>
    );
}
